import type { AuthUserEntityStore } from '@/shared/models';
import { EncryptionService } from './encryptionService';

/**
 * Servicio especializado para encriptación de datos de autenticación
 * Reemplaza: AuthCryptographyService + cryptographyAuthValues.utils
 * Responsabilidad única: Transformar objetos AuthUserEntityStore
 * Cumple Single Responsibility Principle y Open/Closed Principle
 */
export class AuthEncryptionService {
  /**
   * Encripta todos los campos sensibles del objeto de autenticación
   * @param authData - Datos de usuario a encriptar
   * @returns Objeto con campos encriptados
   */
  static encrypt(authData: AuthUserEntityStore): AuthUserEntityStore {
    return {
      // id: authData.id ? EncryptionService.encrypt(String(authData.id)) : null,
      id: authData.id,
      name: EncryptionService.encrypt(authData.name),
      email: EncryptionService.encrypt(authData.email),
      run: EncryptionService.encrypt(authData.run),
      role: EncryptionService.encrypt(authData.role),
      token: EncryptionService.encrypt(authData.token),
      // es_admin: authData.es_admin,
      es_admin: EncryptionService.encryptBoolean(authData.es_admin),
      ultimo_acceso: EncryptionService.encrypt(authData.ultimo_acceso),
      cod_tipo_perfil: authData.cod_tipo_perfil,
      desc_tipo_perfil: EncryptionService.encrypt(authData.desc_tipo_perfil),
    };
  }

  /**
   * Desencripta todos los campos del objeto de autenticación
   * @param encryptedAuthData - Datos encriptados
   * @returns Objeto con campos desencriptados
   */
  static decrypt(encryptedAuthData: AuthUserEntityStore): AuthUserEntityStore {
    return {
      // id: encryptedAuthData.id
      //   ? EncryptionService.decrypt(String(encryptedAuthData.id))
      //   : null,
      id: encryptedAuthData.id,
      name: EncryptionService.decrypt(encryptedAuthData.name),
      email: EncryptionService.decrypt(encryptedAuthData.email),
      run: EncryptionService.decrypt(encryptedAuthData.run),
      role: EncryptionService.decrypt(encryptedAuthData.role),
      token: EncryptionService.decrypt(encryptedAuthData.token),
      // es_admin: encryptedAuthData.es_admin,
      es_admin: EncryptionService.decryptBoolean(encryptedAuthData.es_admin),
      ultimo_acceso: EncryptionService.decrypt(encryptedAuthData.ultimo_acceso),
      cod_tipo_perfil: encryptedAuthData.cod_tipo_perfil,
      desc_tipo_perfil: EncryptionService.decrypt(
        encryptedAuthData.desc_tipo_perfil,
      ),
    };
  }

  /**
   * Verifica si los datos de auth están encriptados
   * @param authData - Datos a verificar
   */
  static isEncrypted(authData: AuthUserEntityStore): boolean {
    return (
      EncryptionService.isEncrypted(authData.token) ||
      EncryptionService.isEncrypted(authData.email)
    );
  }

  /**
   * Procesa datos según el tipo de operación (ENCRYPT/DECRYPT)
   * Mantiene compatibilidad con código existente que usa cryptographyAuthValues
   * @param authData - Datos a procesar
   * @param operation - Tipo de operación
   */
  static processAuthData(
    authData: AuthUserEntityStore,
    operation: 'ENCRYPT' | 'DECRYPT',
  ): AuthUserEntityStore {
    return operation === 'ENCRYPT'
      ? this.encrypt(authData)
      : this.decrypt(authData);
  }
}
